import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { Auth, signInWithEmailAndPassword } from '@angular/fire/auth';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { BRAND_ASSETS } from '../../core/constants/brand-assets';

/**
 * Admin Login Page Component
 * Email/password authentication for internal staff only
 * No signup capability - admin accounts are created by superadmins
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;
  loginForm: FormGroup;
  loading: boolean = false;
  errorMessage: string = '';
  showPassword: boolean = false;
  showResendVerification: boolean = false;
  resendingVerification: boolean = false;
  verificationMessage: string = '';
  private pendingVerificationCredentials: { email: string; password: string } | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private auth: Auth,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    // Check for access denied error from claim guard
    this.route.queryParams.subscribe(params => {
      if (params['error'] === 'access_denied') {
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state || history.state;
        this.errorMessage = state?.['errorMessage'] || 'Access denied. You do not have permission to access the Admin Portal.';
      }

      // Set by the auth interceptor after the Hub rejects a flagged account.
      if (params['reason'] === 'password-reset-required') {
        this.errorMessage = 'We found your account, but you need to set a new password before signing in. Use "Forgot password?" below to get a reset link.';
      }
    });
  }

  /**
   * Handle form submission
   * Checks for 2FA requirement before completing login
   */
  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const { email, password } = this.loginForm.value;

    try {
      // First, sign in with Firebase to verify credentials
      await this.authService.signIn(email, password);

      // Check if user has 2FA enabled by calling backend
      try {
        const response = await firstValueFrom(
          this.http.get<any>(`${environment.apiUrl}/auth/me`)
        );

        if (response?.profile?.is2faEnabled) {
          // User has 2FA enabled, redirect to verification page
          // Sign out temporarily until 2FA is verified
          await this.authService.logout();

          this.router.navigate(['/verify-2fa'], {
            state: { email }
          });
          return;
        }
      } catch (error) {
        console.warn('Could not check 2FA status, proceeding with login', error);
      }

      // No 2FA required, proceed to dashboard
      this.router.navigate(['/dashboard']);
    } catch (error: any) {
      this.errorMessage = error.message || 'Login failed. Please check your credentials.';
      this.showResendVerification = this.errorMessage.includes('verify your email');

      if (this.showResendVerification) {
        // Store credentials for resend flow, then sign out immediately
        this.pendingVerificationCredentials = { email, password };
        await this.authService.logout();
      }
    } finally {
      this.loading = false;
    }
  }

  /**
   * Resend verification email by temporarily re-authenticating.
   * Signs in, sends verification email, then signs out immediately.
   */
  async resendVerificationEmail(): Promise<void> {
    if (!this.pendingVerificationCredentials) return;

    this.resendingVerification = true;
    this.verificationMessage = '';

    try {
      const { email, password } = this.pendingVerificationCredentials;

      // Temporarily sign in to get a currentUser for Firebase's sendEmailVerification
      await signInWithEmailAndPassword(this.auth, email, password);
      await this.authService.resendEmailVerification();

      this.verificationMessage = 'Verification email sent! Please check your inbox.';
      this.showResendVerification = false;
      this.pendingVerificationCredentials = null;
    } catch (error: any) {
      this.verificationMessage = error.message || 'Failed to send verification email. Please try again.';
    } finally {
      // Always sign out regardless of success or failure
      try { await this.authService.logout(); } catch {}
      this.resendingVerification = false;
    }
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Get form control error message
   */
  getErrorMessage(controlName: string): string {
    const control = this.loginForm.get(controlName);

    if (control?.hasError('required')) {
      return `${controlName.charAt(0).toUpperCase() + controlName.slice(1)} is required`;
    }

    if (control?.hasError('email')) {
      return 'Invalid email address';
    }

    if (control?.hasError('minlength')) {
      return 'Password must be at least 6 characters';
    }

    return '';
  }

  /**
   * Check if form control has error
   */
  hasError(controlName: string): boolean {
    const control = this.loginForm.get(controlName);
    return !!(control && control.invalid && control.touched);
  }
}
